/* eslint-disable class-methods-use-this */
import { Language } from '@configs/language';
import DueDateSpan from '@constants/dues';
import { DecimalDigits } from '@constants/enums';
import { StateSpan } from '@constants/states';
import {
  DEPOSIT_TRANSACTION_TYPES,
  TransactionTypes,
} from '@efex-components/payables/PaymentModal/types';
import { ReceivableResponse } from '@efex-services/receivables/types';
import { Avatar } from '@efexteam/components';
import {
  CURRENCY_IDS,
  convertDateToHumanReadableFromJsDate,
  formatCurrency,
} from '@utils/formater';
import { TFunction } from 'i18next';
import { DateTime } from 'luxon';

export interface TableRowsServiceProps {
  id: number;
  rvlName: JSX.Element;
  rvlStatus: JSX.Element;
  rvlShowSingleDetail: boolean;
  rvlAmount?: string;
  rvlDate?: JSX.Element;
  rvlDateRequested?: string;
  rvlAmountToReceive?: string;
  rvlOriginAmount?: string;
  rvlPaymentDue?: JSX.Element;
}

export default class TableConvertData {
  translate: TFunction<'translation', undefined, 'translation'>;

  language: Language;

  constructor(
    translate: TFunction<'translation', undefined, 'translation'>,
    language: Language
  ) {
    this.translate = translate;
    this.language = language;
  }

  private getContactName(contact: ReceivableResponse['contact']): string {
    const { enterprise } = contact ?? {};
    const { name } = enterprise ?? {};
    return name ?? '';
  }

  private getPayerName(movement: ReceivableResponse): string {
    return movement.payer?.payerName ?? this.getContactName(movement.contact);
  }

  /**
   * Retrieves the movement amount as a formatted string based on the transaction type.
   * @param movement - The movement to retrieve the amount from
   * @returns The formatted amount as a string
   */
  private getMovementAmount(movement: ReceivableResponse): string {
    const isExternalDeposit =
      movement.transactionTypeId === TransactionTypes.external_deposit;
    const amount = isExternalDeposit ? movement.desAmount : movement.amount;
    const currencyId = isExternalDeposit
      ? movement.desCurrencyId
      : movement.currencyId;
    const values = formatCurrency(
      amount,
      DecimalDigits.DEFAULT_DIGITS,
      DecimalDigits.DEFAULT_MIN_DIGITS,
      CURRENCY_IDS[currencyId as keyof typeof CURRENCY_IDS]
    );
    return `${values} ${CURRENCY_IDS[currencyId as keyof typeof CURRENCY_IDS]}`;
  }

  /**
   * Retrieves the display name for the movement based on the transaction type.
   * @param movement - The movement to retrieve the display name from
   * @returns The display name as a string
   */
  private getDisplayName(movement: ReceivableResponse): string {
    const isDeposit = DEPOSIT_TRANSACTION_TYPES.includes(
      movement.transactionTypeId
    );
    return isDeposit
      ? this.getPayerName(movement)
      : this.getContactName(movement.contact);
  }

  /**
   * Renders the movement name with an avatar and the display name.
   * @param movement - The movement to render the name from
   * @returns The JSX element with the movement name
   */
  private renderMovementName(movement: ReceivableResponse): JSX.Element {
    const displayName = this.getDisplayName(movement);
    return (
      <section className="flex space-x-2 items-center justify-center">
        <Avatar username={displayName} size="sm" />
        <p className="m-0 ml-1 text-sm inline">{displayName}</p>
      </section>
    );
  }

  private renderDateSpan(status: string, date: string): JSX.Element {
    const formattedDate = convertDateToHumanReadableFromJsDate(
      new Date(date).toString(),
      this.language,
      DateTime.DATE_MED
    );

    return <DueDateSpan state={status} value={formattedDate} />;
  }

  private renderMovementsRow(
    movement: ReceivableResponse
  ): TableRowsServiceProps {
    return {
      id: movement.id,
      rvlName: this.renderMovementName(movement),
      rvlStatus: <StateSpan status={movement.status.name} />,
      rvlAmount: this.getMovementAmount(movement),
      rvlDate: this.renderDateSpan(movement.status.name, movement.dueDate),
      rvlShowSingleDetail: !!movement.contact,
    };
  }

  convertData(data: ReceivableResponse[]): TableRowsServiceProps[] {
    return data.map((row) => this.renderMovementsRow(row));
  }
}
